/**
 * @file Ecroulement d'une maisonnette (decor du hub) : elle se casse vraiment en morceaux et s'effondre sur elle-meme.
 *
 * Chronologie (4,6 s, independante de l'entite, qui disparait apres 0,45 s) :
 *  0.0 - 0.7  grondement : la maison tremble, nuages de poussiere au pied
 *  0.2 - 0.8  la cheminee bascule et se brise en briques
 *  0.45 - 1.2 le toit s'affaisse : ses pans se replient vers l'interieur et s'ecrasent sur le plancher (le faite saute)
 *  1.0 - 1.9  les murs se renversent vers l'exterieur, par morceaux (haut puis bas ; la porte claque vers la camera)
 *  1.9 - 3.2  les debris rebondissent et se calment, le soubassement se tasse
 *  3.4 - 4.6  dernier nuage, les gravats se tassent et fondent vers le centre
 * Tout est fait de pieces reelles (memes primitives et couleurs que le prototype de la maison) : aucune ressource GPU propre (geometries et
 * materiaux viennent des caches partages), seulement des Object3D temporaires.
 */
import { ConeGeometry, ExtrudeGeometry, Group, Quaternion, Shape, Vector3 } from 'three';
import type { BufferGeometry, Mesh, Scene } from 'three';
import type { TargetPalette } from '@config/types';
import { mixHex } from '../factories/MergeBuilder';
import { BRICK, DARK, GLASS, HOUSE_FOUNDATION, HOUSE_WALL, houseChimney, houseColors, PINK, WHITE, YELLOW } from '../factories/ThemeKit';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ToonKit } from '../toon/toon';

/** Duree totale d'un ecroulement (s). */
export const COLLAPSE_SECONDS = 4.6;

const MAX_ACTIVE = 4;
const PUFFS = 18;
const DEBRIS = 40;
const GRAVITY = 11;
const SINK_START = 3.4;
const SINK_END = COLLAPSE_SECONDS;
const UP = new Vector3(0, 1, 0);
const HALF_PI = Math.PI / 2;

/** Impact : acceleration (chute) jusqu'a s = 1, puis petit rebond amorti. */
const impact = (s: number): number => {
  if (s <= 0) return 0;
  if (s < 1) return s * s;
  const d = s - 1;
  return 1 - 0.12 * Math.exp(-6 * d) * Math.abs(Math.sin(11 * d));
};

const mulberry = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export interface CollapseSpec {
  /** Identifiant de l'entite (graine des aleas). */
  id: number;
  /** Position sim (x, y) ; rendu : (x, 0, -y). */
  x: number;
  y: number;
  /** Lacet de la cible. */
  rot: number;
  variant: number;
  /** Echelle monde (def.visual.scale * echelle de l'instance). */
  scale: number;
  palette: TargetPalette;
}

interface Piece {
  g: Group;
  /** Position de repos du pivot. */
  bx: number;
  by: number;
  bz: number;
  axis: Vector3;
  angle: number;
  t0: number;
  dur: number;
  /** Glissement final (applique avec la chute). */
  sx: number;
  sy: number;
  sz: number;
  /** Chute verticale differee (piece posee sur une autre qui s'effondre). */
  dropT0: number;
  dropDur: number;
  drop: number;
  twist: number;
  /** A l'impact : taille du nuage de poussiere (0 = aucun) et nombre d'eclats. */
  dust: number;
  chips: number;
  colors: string[];
  impacted: boolean;
  /** Instant (s) apres lequel la piece est masquee (cheminee brisee). */
  vanishAt: number;
}

interface Debris {
  mesh: Mesh;
  vx: number;
  vy: number;
  vz: number;
  wx: number;
  wy: number;
  wz: number;
  half: number;
  live: boolean;
  asleep: boolean;
  /** Instant de lancement (boule de faite) ; -1 = debris du pool. */
  launchAt: number;
}

interface Puff {
  mesh: Mesh;
  t0: number;
  life: number;
  size: number;
  vx: number;
  vy: number;
  vz: number;
  live: boolean;
}

interface Collapse {
  root: Group;
  rubble: Group;
  pieces: Piece[];
  debris: Debris[];
  /** Pieces lancees a un instant donne (boule de faite). */
  balls: Debris[];
  puffs: Puff[];
  rand: () => number;
  t: number;
  /** Prochain debris / nuage a reutiliser. */
  nextDebris: number;
  nextPuff: number;
  /** Dernier nuage de fin deja lance. */
  poofed: boolean;
}

export class HouseCollapse {
  private readonly active: Collapse[] = [];
  private readonly q = new Quaternion();
  private readonly qt = new Quaternion();

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {}

  get count(): number {
    return this.active.length;
  }

  /** Lance un ecroulement (le plus ancien est termine d'office s'il y en a trop). */
  start(spec: CollapseSpec): void {
    if (this.active.length >= MAX_ACTIVE) {
      const old = this.active.shift();
      if (old) this.scene.remove(old.root);
    }
    const c = this.build(spec);
    this.active.push(c);
    this.scene.add(c.root);
    c.root.position.set(spec.x, 0, -spec.y);
    c.root.rotation.y = spec.rot;
    c.root.scale.setScalar(spec.scale);
    this.dust(c, 0.9, 0.1, 0.9, 1.1);
    this.dust(c, -0.9, 0.1, 0.6, 0.9);
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const c = this.active[i];
      if (!c) continue;
      c.t += dt;
      if (c.t >= COLLAPSE_SECONDS) {
        this.scene.remove(c.root);
        this.active.splice(i, 1);
        continue;
      }
      this.step(c, dt);
    }
  }

  dispose(): void {
    for (const c of this.active) this.scene.remove(c.root);
    this.active.length = 0;
  }

  // ------------------------------------------------------------------------------------------------------------------ simulation

  private step(c: Collapse, dt: number): void {
    const t = c.t;
    // grondement : tremblement qui monte puis retombe
    const amp = t < 0.5 ? t / 0.5 : t < 1.3 ? 1 - (t - 0.5) / 0.8 : 0;
    const jx = Math.sin(t * 61) * 0.035 * amp;
    const jz = Math.cos(t * 53) * 0.035 * amp;
    c.rubble.position.x = jx;
    c.rubble.position.z = jz;
    // fin : un dernier nuage, puis les gravats se tassent et fondent (reduction vers le centre, comme les arbres)
    const sink = t < SINK_START ? 0 : (t - SINK_START) / (SINK_END - SINK_START);
    if (sink > 0 && !c.poofed) {
      c.poofed = true;
      this.dust(c, 0.6, 0.1, 0.6, 1.0);
      this.dust(c, -0.6, 0.1, -0.5, 1.0);
      this.dust(c, 0.0, 0.1, 0.9, 0.9);
    }
    c.rubble.scale.setScalar(Math.max(0.001, 1 - sink * sink));
    c.rubble.position.y = -0.15 * sink;

    for (const p of c.pieces) {
      if (t >= p.vanishAt) {
        p.g.visible = false;
        continue;
      }
      const s = (t - p.t0) / p.dur;
      if (s <= 0) {
        // avant sa chute : legers soubresauts pendant le grondement
        const k = Math.sin(t * 47 + p.bx * 5) * 0.012 * amp;
        p.g.position.set(p.bx, p.by + Math.abs(k), p.bz);
        continue;
      }
      const f = impact(s);
      this.q.setFromAxisAngle(p.axis, p.angle * f);
      if (p.twist !== 0) {
        this.qt.setFromAxisAngle(UP, p.twist * f);
        this.q.premultiply(this.qt);
      }
      p.g.quaternion.copy(this.q);
      const d = p.drop === 0 ? 0 : p.drop * impact((t - p.dropT0) / p.dropDur);
      p.g.position.set(p.bx + p.sx * f, p.by + p.sy * f - d, p.bz + p.sz * f);
      if (!p.impacted && s >= 1) {
        p.impacted = true;
        if (p.dust > 0) this.dust(c, p.bx + p.sx * 0.5, 0.12, p.bz + p.sz * 0.5, p.dust);
        if (p.chips > 0) this.burst(c, p.bx + p.sx * 0.5, Math.max(0.2, p.by + p.sy * 0.5 - p.drop), p.bz + p.sz * 0.5, p.chips, p.colors, p.sx, p.sz);
      }
    }
    for (const b of c.balls) {
      if (!b.live && t >= b.launchAt) {
        b.live = true;
        b.asleep = false;
      }
    }
    this.stepDebris(c, c.debris, dt);
    this.stepDebris(c, c.balls, dt);
    this.stepPuffs(c);
  }

  private stepDebris(_c: Collapse, list: Debris[], dt: number): void {
    for (const d of list) {
      if (!d.live) continue;
      if (!d.asleep) {
        d.vy -= GRAVITY * dt;
        const m = d.mesh;
        m.position.x += d.vx * dt;
        m.position.y += d.vy * dt;
        m.position.z += d.vz * dt;
        m.rotation.x += d.wx * dt;
        m.rotation.y += d.wy * dt;
        m.rotation.z += d.wz * dt;
        if (m.position.y <= d.half) {
          m.position.y = d.half;
          if (Math.abs(d.vy) < 1.1) {
            d.asleep = true;
          } else {
            d.vy = -d.vy * 0.38;
            d.vx *= 0.62;
            d.vz *= 0.62;
            d.wx *= 0.6;
            d.wy *= 0.6;
            d.wz *= 0.6;
          }
        }
      }
    }
  }

  private stepPuffs(c: Collapse): void {
    for (const p of c.puffs) {
      if (!p.live) continue;
      const k = (c.t - p.t0) / p.life;
      if (k >= 1) {
        p.live = false;
        p.mesh.visible = false;
        continue;
      }
      // grossit vite, puis se resorbe
      const grow = k < 0.35 ? k / 0.35 : 1 - (k - 0.35) / 0.65;
      const e = grow * (2 - grow);
      p.mesh.scale.setScalar(Math.max(0.001, p.size * e));
      p.mesh.position.set(p.mesh.position.x + p.vx * 0.016, p.mesh.position.y + p.vy * 0.016, p.mesh.position.z + p.vz * 0.016);
    }
  }

  private dust(c: Collapse, x: number, y: number, z: number, size: number): void {
    for (let n = 0; n < 2; n++) {
      const p = c.puffs[c.nextPuff];
      c.nextPuff = (c.nextPuff + 1) % c.puffs.length;
      if (!p) return;
      const a = c.rand() * Math.PI * 2;
      const r = c.rand() * 0.25 * size;
      p.live = true;
      p.t0 = c.t;
      p.life = 0.9 + c.rand() * 0.5;
      p.size = size * (0.18 + c.rand() * 0.12);
      p.vx = Math.cos(a) * (0.5 + c.rand() * 0.6);
      p.vz = Math.sin(a) * (0.5 + c.rand() * 0.6);
      p.vy = 0.7 + c.rand() * 0.6;
      p.mesh.position.set(x + Math.cos(a) * r, y + 0.1, z + Math.sin(a) * r);
      p.mesh.scale.setScalar(0.001);
      p.mesh.visible = true;
    }
  }

  private burst(c: Collapse, x: number, y: number, z: number, count: number, colors: string[], dirX: number, dirZ: number): void {
    const len = Math.hypot(dirX, dirZ) || 1;
    for (let n = 0; n < count; n++) {
      const d = c.debris[c.nextDebris];
      c.nextDebris = (c.nextDebris + 1) % c.debris.length;
      if (!d) return;
      const color = colors[Math.floor(c.rand() * colors.length)] ?? WHITE;
      d.mesh.material = this.kit.toon(color);
      const size = 0.06 + c.rand() * 0.1;
      d.half = size * 0.5;
      d.mesh.scale.set(size * (0.8 + c.rand() * 1.6), size * (0.6 + c.rand() * 0.8), size * (0.8 + c.rand()));
      d.mesh.position.set(x, y, z);
      d.mesh.rotation.set(c.rand() * 6, c.rand() * 6, c.rand() * 6);
      const outward = 1.2 + c.rand() * 2.4;
      d.vx = (dirX / len) * outward + (c.rand() - 0.5) * 2.2;
      d.vz = (dirZ / len) * outward + (c.rand() - 0.5) * 2.2;
      d.vy = 2.2 + c.rand() * 3.2;
      d.wx = (c.rand() - 0.5) * 14;
      d.wy = (c.rand() - 0.5) * 14;
      d.wz = (c.rand() - 0.5) * 14;
      d.live = true;
      d.asleep = false;
      d.mesh.visible = true;
    }
  }

  // ------------------------------------------------------------------------------------------------------------------ construction

  private build(spec: CollapseSpec): Collapse {
    const { palette: p } = spec;
    const v = ((Math.floor(spec.variant) % 3) + 3) % 3;
    const rand = mulberry(spec.id * 7919 + v * 131 + 17);
    const root = new Group();
    const rubble = new Group();
    root.add(rubble);
    const c: Collapse = { root, rubble, pieces: [], debris: [], balls: [], puffs: [], rand, t: 0, nextDebris: 0, nextPuff: 0, poofed: false };

    const col = houseColors(p, v);
    const wood = p.trunk ?? '#a9794a';
    const W = 2.0;
    const D = 1.8;
    const H = HOUSE_WALL;
    const F = HOUSE_FOUNDATION;
    const top = F + H;
    const T = 0.14;
    const chips = [col.wall, col.roof, wood, BRICK];

    // --- pieces fixes : soubassement (se tasse) + plancher
    const baseGroup = new Group();
    if (v === 2) {
      baseGroup.add(this.mesh(this.geo.cylinder(1.12, 1.12, F, 16), col.base, 0, F / 2, 0));
      baseGroup.add(this.mesh(this.geo.cylinder(0.92, 0.92, 0.05, 16), wood, 0, F + 0.025, 0));
    } else {
      baseGroup.add(this.mesh(this.geo.box(W + 0.12, F, D + 0.12), col.base, 0, F / 2, 0));
      baseGroup.add(this.mesh(this.geo.box(W - 0.1, 0.05, D - 0.1), wood, 0, F + 0.025, 0));
      baseGroup.add(this.mesh(this.geo.box(0.9, 0.1, 0.42), mixHex(col.base, WHITE, 0.25), 0.38, 0.05, D / 2 + 0.42));
    }
    c.rubble.add(baseGroup);
    c.pieces.push(this.pieceOf(baseGroup, 0, 0, 0, { axis: new Vector3(1, 0, 0), angle: 0.04, t0: 1.9, dur: 0.6, sy: -0.04, dust: 0, chips: 0, colors: chips }));

    // --- cheminee : bascule vers +X puis se brise en briques
    const ch = houseChimney(v);
    const chimney = new Group();
    chimney.add(this.mesh(this.geo.box(0.32, ch.h, 0.32), BRICK, ch.x, ch.y, ch.z));
    chimney.add(this.mesh(this.geo.box(0.42, 0.08, 0.42), mixHex(BRICK, DARK, 0.25), ch.x, ch.y + ch.h / 2, ch.z));
    const chBaseY = ch.y - ch.h / 2;
    this.addPiece(c, chimney, ch.x, chBaseY, ch.z, { axis: new Vector3(0, 0, -1), angle: 1.15, t0: 0.2, dur: 0.6, sx: 0.35, sy: -0.25, dust: 0.5, chips: 7, colors: [BRICK, mixHex(BRICK, DARK, 0.3), WHITE], twist: 0.2, vanish: true });

    // --- murs, toit
    if (v === 2) this.buildRound(c, p, col, wood, top, F, H);
    else this.buildFlat(c, p, col, wood, v, W, D, H, F, top, T);

    // --- reserves de debris et de poussiere (reutilisees)
    for (let i = 0; i < DEBRIS; i++) {
      const m = this.kit.toonMesh(this.geo.box(1, 1, 1), BRICK, { outlinePx: 1.5 });
      m.visible = false;
      c.rubble.add(m);
      c.debris.push({ mesh: m, vx: 0, vy: 0, vz: 0, wx: 0, wy: 0, wz: 0, half: 0.05, live: false, asleep: true, launchAt: -1 });
    }
    for (let i = 0; i < PUFFS; i++) {
      const m = this.kit.toonMesh(this.geo.ico(1, 1), i % 2 === 0 ? '#e8dcc4' : '#cdbd9f', { outlinePx: 2 });
      m.visible = false;
      c.root.add(m);
      c.puffs.push({ mesh: m, t0: 0, life: 1, size: 1, vx: 0, vy: 0, vz: 0, live: false });
    }
    return c;
  }

  /** Maisons a murs plats : toit en pagode (0) ou a pignon (1). */
  private buildFlat(c: Collapse, p: TargetPalette, col: { wall: string; roof: string; base: string }, wood: string, v: number, W: number, D: number, H: number, F: number, top: number, T: number): void {
    const front = D / 2;
    const rand = c.rand;
    const chips = [col.wall, col.roof, wood];
    const split = 0.75;
    const lowH = H * split;
    const upH = H - lowH;

    // facade : moitie fenetre (gauche) et moitie porte (droite) ; la porte claque vers la camera
    for (const side of [-1, 1] as const) {
      const g = new Group();
      const cx = side * (W / 4);
      g.add(this.mesh(this.geo.box(W / 2, H, T), col.wall, cx, F + H / 2, front - T / 2));
      if (side === 1) this.door(g, p, wood, 0.38, F, front, col.base, false);
      else this.window(g, p, wood, -0.55, F, front, false);
      this.addPiece(c, g, cx, F, front, { axis: new Vector3(1, 0, 0), angle: HALF_PI, t0: side === 1 ? 1.15 : 1.4, dur: 0.6, sz: 0.25, sy: 0.04, dust: 1.0, chips: 5, colors: chips, twist: side * 0.12 * (rand() - 0.2) });
    }
    // murs arriere, gauche, droit : haut puis bas
    const walls: Array<{ n: [number, number]; len: number; cx: number; cz: number; t: number }> = [
      { n: [0, -1], len: W, cx: 0, cz: -D / 2 + T / 2, t: 1.0 },
      { n: [-1, 0], len: D, cx: -W / 2 + T / 2, cz: 0, t: 1.1 },
      { n: [1, 0], len: D, cx: W / 2 - T / 2, cz: 0, t: 1.2 },
    ];
    for (const w of walls) {
      const [nx, nz] = w.n;
      const along = nx !== 0; // le mur s'etend le long de z
      const sizeX = along ? T : w.len;
      const sizeZ = along ? w.len : T;
      const axis = new Vector3(nz, 0, -nx);
      const edgeX = w.cx + nx * (T / 2);
      const edgeZ = w.cz + nz * (T / 2);
      // bas
      const low = new Group();
      low.add(this.mesh(this.geo.box(sizeX, lowH, sizeZ), col.wall, w.cx, F + lowH / 2, w.cz));
      if (nx === 1) {
        low.add(this.mesh(this.geo.box(0.06, 0.4, 0.4), p.accent, W / 2 + 0.01, F + 0.6, 0.1));
        low.add(this.mesh(this.geo.box(0.07, 0.28, 0.28), GLASS, W / 2 + 0.03, F + 0.6, 0.1));
      }
      this.addPiece(c, low, edgeX, F, edgeZ, { axis, angle: HALF_PI, t0: w.t + 0.3, dur: 0.6, sx: nx * 0.3, sz: nz * 0.3, dust: 1.0, chips: 4, colors: chips, twist: (rand() - 0.5) * 0.25 });
      // haut : tombe d'abord, puis redescend quand le bas s'effondre
      const up = new Group();
      up.add(this.mesh(this.geo.box(sizeX, upH, sizeZ), col.wall, w.cx, F + lowH + upH / 2, w.cz));
      const upY = F + lowH;
      this.addPiece(c, up, edgeX, upY, edgeZ, { axis, angle: HALF_PI, t0: w.t, dur: 0.5, sx: nx * 0.2, sz: nz * 0.2, dropT0: w.t + 0.45, dropDur: 0.35, drop: upY - 0.08, dust: 0.7, chips: 3, colors: chips, twist: (rand() - 0.5) * 0.3 });
    }
    // facade : le haut de la facade est dans les moities ci-dessus (rien de plus)
    void top;

    if (v === 0) {
      // toit en pagode : 4 pans bas (avant-toit) qui se replient vers l'interieur, pyramide haute qui s'ecrase, boule au faite qui rebondit
      for (let i = 0; i < 4; i++) {
        const thetaC = (i + 0.5) * HALF_PI + Math.PI / 4;
        const nx = Math.round(Math.sin(thetaC));
        const nz = Math.round(Math.cos(thetaC));
        const g = new Group();
        const wedge = this.geo.get(`hc:eave:${i}`, () => new ConeGeometry(1.5, 0.5, 1, 1, false, i * HALF_PI, HALF_PI));
        const px = nx * 1.06;
        const pz = nz * 1.06;
        g.add(this.mesh(wedge, col.roof, 0, top + 0.25, 0, Math.PI / 4));
        this.addPiece(c, g, px, top, pz, { axis: new Vector3(nz, 0, -nx), angle: -0.42, t0: 0.55 + i * 0.07, dur: 0.5, sx: -nx * 0.45, sz: -nz * 0.45, sy: 0, dropT0: 0.62 + i * 0.07, dropDur: 0.45, drop: top - F - 0.12, dust: 0.9, chips: 3, colors: [col.roof, mixHex(col.roof, WHITE, 0.22)], twist: (rand() - 0.5) * 0.3 });
      }
      const upper = new Group();
      upper.add(this.mesh(this.geo.cone(1.0, 0.62, 4), mixHex(col.roof, WHITE, 0.22), 0, top + 0.7, 0, Math.PI / 4));
      this.addPiece(c, upper, 0, top, 0, { axis: new Vector3(1, 0, 0.4), angle: 0.22, t0: 0.5, dur: 0.6, dropT0: 0.5, dropDur: 0.6, drop: top - F - 0.18, dust: 1.1, chips: 6, colors: [col.roof, mixHex(col.roof, WHITE, 0.22), p.accent], twist: 0.5 });
      const ball = new Group();
      ball.add(this.mesh(this.geo.sphere(0.1, 8, 6), p.accent, 0, 0, 0));
      this.addBall(c, ball, 0, top + 1.03, 0, 0.5);
    } else {
      // toit a pignon : deux pans qui s'ecrasent vers l'interieur, deux pignons (avant / arriere) qui se renversent
      const ang = Math.atan2(0.58, (W + 0.24) / 2);
      const slope = Math.hypot(0.58, (W + 0.24) / 2);
      const len = D + 0.2;
      for (const side of [-1, 1] as const) {
        const g = new Group();
        const dirX = -side * Math.cos(ang);
        const dirY = Math.sin(ang);
        const eaveX = side * ((W + 0.24) / 2);
        g.add(this.mesh(this.geo.box(slope, 0.1, len), col.roof, eaveX + dirX * (slope / 2), top - 0.02 + dirY * (slope / 2), 0, 0, 0, Math.atan2(dirY, dirX)));
        this.addPiece(c, g, eaveX, top - 0.02, 0, { axis: new Vector3(0, 0, side), angle: ang + 0.08, t0: 0.5 + (side === 1 ? 0.08 : 0), dur: 0.55, sx: -side * 0.4, dropT0: 0.62, dropDur: 0.45, drop: top - F - 0.14, dust: 1.1, chips: 4, colors: [col.roof, mixHex(col.roof, DARK, 0.2)], twist: (rand() - 0.5) * 0.25 });
      }
      const sxg = (W + 0.24) / 1.732;
      const sz = 0.58;
      for (const zs of [1, -1] as const) {
        const g = new Group();
        const prism = this.geo.cylinder(1, 1, 1, 3);
        const m = this.mesh(prism, col.wall, 0, top + 0.5 * sz - 0.02, zs * (D / 2 + 0.02), 0, -HALF_PI, 0);
        m.scale.set(sxg * 0.9, 0.1, sz * 0.9);
        g.add(m);
        if (zs === 1) g.add(this.mesh(this.geo.cylinder(0.13, 0.13, 0.04, 12), p.accent, 0, top + 0.22, D / 2 + 0.05, 0, HALF_PI));
        this.addPiece(c, g, 0, top, zs * (D / 2), { axis: new Vector3(zs, 0, 0), angle: HALF_PI, t0: 0.95, dur: 0.5, sz: zs * 0.3, dropT0: 1.3, dropDur: 0.3, drop: top - 0.1, dust: 0.8, chips: 3, colors: chips, twist: 0 });
      }
    }
  }

  /** Tourelle ronde : 4 pans de mur en arcs et 4 pans de toit conique. */
  private buildRound(c: Collapse, p: TargetPalette, col: { wall: string; roof: string; base: string }, wood: string, top: number, F: number, H: number): void {
    const rand = c.rand;
    const chips = [col.wall, col.roof, wood];
    for (let i = 0; i < 4; i++) {
      const a0 = i * HALF_PI - Math.PI / 4;
      const thetaC = a0 + Math.PI / 4;
      const nx = Math.sin(thetaC);
      const nz = Math.cos(thetaC);
      const g = new Group();
      const arc = this.geo.get(`hc:arc:${i}`, () => arcSolid(a0, a0 + HALF_PI, 0.9, 1.02, H));
      g.add(this.mesh(arc, col.wall, 0, F, 0));
      const front = i === 0;
      if (front) {
        this.door(g, p, wood, 0.32, F, Math.sqrt(1 - 0.32 * 0.32), col.base, true);
        this.window(g, p, wood, -0.55, F, Math.sqrt(1 - 0.55 * 0.55), true);
      }
      this.addPiece(c, g, nx * 1.02, F, nz * 1.02, { axis: new Vector3(nz, 0, -nx), angle: HALF_PI, t0: front ? 1.15 : 1.0 + i * 0.12, dur: 0.65, sx: nx * 0.3, sz: nz * 0.3, dust: 1.0, chips: 4, colors: chips, twist: (rand() - 0.5) * 0.3 });
    }
    // toit conique : 4 pans qui se replient vers l'interieur
    for (let i = 0; i < 4; i++) {
      const a0 = i * HALF_PI - Math.PI / 4;
      const thetaC = a0 + Math.PI / 4;
      const nx = Math.sin(thetaC);
      const nz = Math.cos(thetaC);
      const g = new Group();
      const wedge = this.geo.get(`hc:cone:${i}`, () => new ConeGeometry(1.28, 1.05, 5, 1, false, a0 + 0, HALF_PI));
      g.add(this.mesh(wedge, col.roof, 0, top + 0.52, 0));
      this.addPiece(c, g, nx * 1.15, top, nz * 1.15, { axis: new Vector3(nz, 0, -nx), angle: -0.6, t0: 0.5 + i * 0.08, dur: 0.55, sx: -nx * 0.5, sz: -nz * 0.5, dropT0: 0.6 + i * 0.08, dropDur: 0.5, drop: top - F - 0.14, dust: 1.0, chips: 3, colors: [col.roof, mixHex(col.roof, DARK, 0.2)], twist: (rand() - 0.5) * 0.3 });
    }
    const brim = new Group();
    brim.add(this.mesh(this.geo.cylinder(1.1, 1.1, 0.07, 16), mixHex(col.roof, DARK, 0.15), 0, top + 0.02, 0));
    this.addPiece(c, brim, 0, top, 0, { axis: new Vector3(1, 0, 0.3), angle: 0.18, t0: 0.5, dur: 0.6, dropT0: 0.5, dropDur: 0.6, drop: top - F - 0.1, dust: 0.9, chips: 0, colors: chips, twist: 0.4 });
    const ball = new Group();
    ball.add(this.mesh(this.geo.sphere(0.11, 8, 6), p.accent, 0, 0, 0));
    this.addBall(c, ball, 0, top + 1.08, 0, 0.5);
  }

  // ------------------------------------------------------------------------------------------------------------------ petits outils

  /** Maillage toon + contour, place en coordonnees de maison (rotations en radians). */
  private mesh(g: BufferGeometry, color: string, x: number, y: number, z: number, ry = 0, rx = 0, rz = 0): Mesh {
    const m = this.kit.toonMesh(g, color, { outlinePx: 2 });
    m.position.set(x, y, z);
    m.rotation.set(rx, ry, rz);
    return m;
  }

  /** Cree le pivot en (px, py, pz) : les enfants du groupe ont ete poses en coordonnees de maison, on les decale de -pivot. */
  private pieceOf(g: Group, px: number, py: number, pz: number, o: Partial<Piece>): Piece {
    for (const child of g.children) child.position.set(child.position.x - px, child.position.y - py, child.position.z - pz);
    g.position.set(px, py, pz);
    return {
      g,
      bx: px,
      by: py,
      bz: pz,
      axis: o.axis ?? new Vector3(1, 0, 0),
      angle: o.angle ?? 0,
      t0: o.t0 ?? 0,
      dur: o.dur ?? 0.5,
      sx: o.sx ?? 0,
      sy: o.sy ?? 0,
      sz: o.sz ?? 0,
      dropT0: o.dropT0 ?? 0,
      dropDur: o.dropDur ?? 0.3,
      drop: o.drop ?? 0,
      twist: o.twist ?? 0,
      dust: o.dust ?? 0,
      chips: o.chips ?? 0,
      colors: o.colors ?? [],
      impacted: false,
      vanishAt: Infinity,
    };
  }

  private addPiece(c: Collapse, g: Group, px: number, py: number, pz: number, o: Partial<Piece> & { vanish?: boolean }): void {
    const piece = this.pieceOf(g, px, py, pz, o);
    // la cheminee disparait apres s'etre brisee en briques
    if (o.vanish) piece.vanishAt = piece.t0 + piece.dur + 0.1;
    c.rubble.add(g);
    c.pieces.push(piece);
  }

  /** Boule de faite : posee sur le toit, projetee en l'air a l'instant `at`, puis rebondit au sol comme un debris. */
  private addBall(c: Collapse, g: Group, x: number, y: number, z: number, at: number): void {
    const m = g.children[0] as Mesh | undefined;
    if (!m) return;
    m.position.set(x, y, z);
    c.rubble.add(m);
    c.balls.push({ mesh: m, vx: 1.1, vy: 4.2, vz: 0.7, wx: 6, wy: 3, wz: 5, half: 0.1, live: false, asleep: true, launchAt: at });
  }

  private door(g: Group, p: TargetPalette, wood: string, ax: number, F: number, fz: number, base: string, round: boolean): void {
    void base;
    const yaw = round ? Math.atan2(ax, fz) : 0;
    const co = Math.cos(yaw);
    const sn = Math.sin(yaw);
    const at = (lx: number, ly: number, lz: number, geom: BufferGeometry, color: string, rx = 0): void => {
      g.add(this.mesh(geom, color, ax + lx * co + lz * sn, ly, fz - lx * sn + lz * co, yaw, rx, 0));
    };
    at(0, F + 0.4, 0.02, this.geo.box(0.7, 0.8, 0.06), p.accent);
    at(0, F + 0.8, 0.02, this.geo.cylinder(0.35, 0.35, 0.06, 14), p.accent, HALF_PI);
    at(0, F + 0.38, 0.06, this.geo.box(0.54, 0.76, 0.06), wood);
    at(0, F + 0.76, 0.06, this.geo.cylinder(0.27, 0.27, 0.06, 14), wood, HALF_PI);
    at(0.16, F + 0.4, 0.12, this.geo.sphere(0.05, 6, 5), p.accent);
  }

  private window(g: Group, p: TargetPalette, wood: string, ax: number, F: number, fz: number, round: boolean): void {
    const yaw = round ? Math.atan2(ax, fz) : 0;
    const co = Math.cos(yaw);
    const sn = Math.sin(yaw);
    const at = (lx: number, ly: number, lz: number, geom: BufferGeometry, color: string): void => {
      g.add(this.mesh(geom, color, ax + lx * co + lz * sn, ly, fz - lx * sn + lz * co, yaw));
    };
    at(0, F + 0.68, 0.02, this.geo.box(0.56, 0.56, 0.06), p.accent);
    at(0, F + 0.68, 0.06, this.geo.box(0.42, 0.42, 0.05), GLASS);
    at(0, F + 0.68, 0.09, this.geo.box(0.05, 0.42, 0.04), p.accent);
    at(0, F + 0.68, 0.09, this.geo.box(0.42, 0.05, 0.04), p.accent);
    at(0, F + 0.36, 0.1, this.geo.box(0.66, 0.1, 0.14), wood);
    [-0.22, 0, 0.22].forEach((fx, i) => at(fx, F + 0.46, 0.1, this.geo.sphere(0.08, 6, 5), i === 1 ? YELLOW : PINK));
  }
}

/** Secteur d'anneau plein (mur de tourelle) de a0 a a1, rayons r0..r1, hauteur h, pose sur y = 0 : coordonnees (x, z) = (r sin t, r cos t). */
const arcSolid = (a0: number, a1: number, r0: number, r1: number, h: number): BufferGeometry => {
  const s = new Shape();
  const n = 5;
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    const x = r1 * Math.sin(a);
    const y = -r1 * Math.cos(a);
    if (i === 0) s.moveTo(x, y);
    else s.lineTo(x, y);
  }
  for (let i = n; i >= 0; i--) {
    const a = a0 + ((a1 - a0) * i) / n;
    s.lineTo(r0 * Math.sin(a), -r0 * Math.cos(a));
  }
  const g = new ExtrudeGeometry(s, { depth: h, bevelEnabled: false, curveSegments: 1 });
  g.rotateX(-HALF_PI);
  return g;
};
